import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import {
  fetchPortalAccessRequests,
  approvePortalAccessRequest,
  rejectPortalAccessRequest,
} from '../../../services/portalAccessRequestService';

const STATUS_CONFIG = {
  pendiente:  { label: 'Pendiente',  bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-500' },
  aprobado:   { label: 'Aprobado',   bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: 'bg-green-500' },
  rechazado:  { label: 'Rechazado',  bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: 'bg-red-500' },
};

const formatFecha = (ts) => {
  if (!ts) return '—';
  return new Date(ts)?.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── Detalle / Acción Modal ───────────────────────────────────────────────────
const DetalleModal = ({ solicitud, onClose, onApprove, onReject }) => {
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const sta = STATUS_CONFIG?.[solicitud?.status] || STATUS_CONFIG?.pendiente;
  const isPendiente = solicitud?.status === 'pendiente';

  const handleApprove = async () => {
    setSaving(true);
    setActionError('');
    try {
      const res = await onApprove(solicitud?.id);
      if (!res?.success) {
        setActionError(res?.error || 'Error al aprobar.');
      } else {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    setSaving(true);
    setActionError('');
    try {
      const res = await onReject(solicitud?.id);
      if (!res?.success) {
        setActionError(res?.error || 'Error al rechazar.');
      } else {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="UserCheck" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Solicitud de acceso portal</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Estado */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${sta?.bg} ${sta?.text} ${sta?.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${sta?.dot}`} />
              {sta?.label}
            </span>
            {solicitud?.matched_cliente_id && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                <Icon name="Link" size={10} />
                Cliente existente
              </span>
            )}
          </div>

          {/* Datos del solicitante */}
          <div className="bg-muted/40 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Icon name="User" size={14} className="text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Nombre</p>
                <p className="text-sm font-semibold text-foreground">{solicitud?.nombre || '—'}</p>
              </div>
            </div>
            {solicitud?.telefono && (
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={14} className="text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Teléfono</p>
                  <p className="text-sm text-foreground">{solicitud?.telefono}</p>
                </div>
              </div>
            )}
            {solicitud?.email && (
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Correo</p>
                  <p className="text-sm text-foreground">{solicitud?.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Coincidencia con cliente */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Coincidencia con cliente</p>
            {solicitud?.matched_cliente_id ? (
              <p className="text-sm text-green-700 font-medium flex items-center gap-1.5">
                <Icon name="CheckCircle" size={14} />
                Cliente encontrado — se vinculará al aprobar
              </p>
            ) : (
              <p className="text-sm text-amber-700 flex items-center gap-1.5">
                <Icon name="AlertCircle" size={14} />
                Sin coincidencia — cliente nuevo pendiente de alta
              </p>
            )}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
            <div>
              <p className="font-medium mb-0.5">Recibida</p>
              <p>{formatFecha(solicitud?.created_at)}</p>
            </div>
            {solicitud?.reviewed_at && (
              <div>
                <p className="font-medium mb-0.5">Revisada</p>
                <p>{formatFecha(solicitud?.reviewed_at)}</p>
              </div>
            )}
          </div>

          {/* Error de acción */}
          {actionError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
              <Icon name="AlertCircle" size={14} />
              {actionError}
            </div>
          )}

          {/* Acciones */}
          {isPendiente ? (
            <div className="flex gap-3 pt-2 border-t border-border">
              <button
                onClick={handleReject}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg border border-red-200 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="XCircle" size={14} />}
                Rechazar
              </button>
              <button
                onClick={handleApprove}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="CheckCircle" size={14} />}
                Aprobar
              </button>
            </div>
          ) : (
            <div className="pt-2 border-t border-border">
              <button
                onClick={onClose}
                className="w-full px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Solicitud Row ────────────────────────────────────────────────────────────
const SolicitudRow = ({ solicitud, onAbrir }) => {
  const sta = STATUS_CONFIG?.[solicitud?.status] || STATUS_CONFIG?.pendiente;
  const isPendiente = solicitud?.status === 'pendiente';

  return (
    <div
      onClick={() => onAbrir(solicitud)}
      className={`cursor-pointer rounded-lg border p-4 transition-all hover:shadow-md flex flex-col sm:flex-row sm:items-center gap-3 ${
        isPendiente
          ? 'border-yellow-300 bg-yellow-50/50 hover:bg-yellow-50' :'border-border bg-card hover:bg-muted/30'
      }`}
    >
      {/* Nombre + contacto */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-semibold text-foreground truncate">{solicitud?.nombre || '—'}</p>
          {isPendiente && (
            <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          {solicitud?.telefono && (
            <span className="flex items-center gap-1">
              <Icon name="Phone" size={10} />
              {solicitud?.telefono}
            </span>
          )}
          {solicitud?.email && (
            <span className="flex items-center gap-1">
              <Icon name="Mail" size={10} />
              {solicitud?.email}
            </span>
          )}
        </div>
      </div>

      {/* Coincidencia */}
      <div className="flex-shrink-0 text-xs">
        {solicitud?.matched_cliente_id ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
            <Icon name="Link" size={9} />
            Cliente existente
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
            <Icon name="UserPlus" size={9} />
            Sin coincidencia
          </span>
        )}
      </div>

      {/* Fecha */}
      <div className="flex-shrink-0 text-xs text-muted-foreground hidden sm:block">
        {formatFecha(solicitud?.created_at)}
      </div>

      {/* Estado */}
      <div className="flex-shrink-0">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${sta?.bg} ${sta?.text} ${sta?.border}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${sta?.dot}`} />
          {sta?.label}
        </span>
      </div>

      <Icon name="ChevronRight" size={14} className="text-muted-foreground flex-shrink-0 hidden sm:block" />
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const SolicitudesPortalPanel = ({ companyId }) => {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('pendiente'); // 'pendiente' | 'todas'

  const cargarSolicitudes = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchPortalAccessRequests(companyId);
      if (!res?.success) {
        setError(res?.error || 'No se pudieron cargar las solicitudes.');
      } else {
        setSolicitudes(res?.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    cargarSolicitudes();
  }, [cargarSolicitudes]);

  const handleApprove = async (requestId) => {
    const res = await approvePortalAccessRequest(requestId, user?.id);
    if (res?.success) {
      await cargarSolicitudes();
    }
    return res;
  };

  const handleReject = async (requestId) => {
    const res = await rejectPortalAccessRequest(requestId, user?.id);
    if (res?.success) {
      await cargarSolicitudes();
    }
    return res;
  };

  const filtradas = filtroStatus === 'pendiente'
    ? solicitudes?.filter(s => s?.status === 'pendiente')
    : solicitudes;

  const pendienteCount = solicitudes?.filter(s => s?.status === 'pendiente')?.length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="UserCheck" size={18} className="text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Solicitudes de acceso portal</h2>
            <p className="text-xs text-muted-foreground">
              {pendienteCount > 0
                ? `${pendienteCount} pendiente${pendienteCount !== 1 ? 's' : ''} de revisión`
                : 'Sin solicitudes pendientes'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Filtro */}
          <div className="flex rounded-lg border border-border overflow-hidden text-xs font-medium">
            <button
              onClick={() => setFiltroStatus('pendiente')}
              className={`px-3 py-1.5 transition-colors ${filtroStatus === 'pendiente' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted'}`}
            >
              Pendientes
              {pendienteCount > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${filtroStatus === 'pendiente' ? 'bg-white/20' : 'bg-yellow-100 text-yellow-700'}`}>
                  {pendienteCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setFiltroStatus('todas')}
              className={`px-3 py-1.5 transition-colors ${filtroStatus === 'todas' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted'}`}
            >
              Todas
            </button>
          </div>

          <button
            onClick={cargarSolicitudes}
            disabled={loading}
            className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
            title="Recargar"
          >
            <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-sm text-red-700">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
        </div>
      )}
      {/* Empty */}
      {!loading && !error && filtradas?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <Icon name="UserCheck" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">
            {filtroStatus === 'pendiente' ? 'Sin solicitudes pendientes' : 'Sin solicitudes registradas'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Las solicitudes de acceso al portal aparecerán aquí.
          </p>
        </div>
      )}
      {/* Lista */}
      {!loading && filtradas?.length > 0 && (
        <div className="space-y-2">
          {filtradas?.map((sol) => (
            <SolicitudRow
              key={sol?.id}
              solicitud={sol}
              onAbrir={setDetalle}
            />
          ))}
        </div>
      )}
      {/* Detalle Modal */}
      {detalle && (
        <DetalleModal
          solicitud={detalle}
          onClose={() => setDetalle(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

export default SolicitudesPortalPanel;
